import {
	ActivateProductCcpResponse,
	ActivateProductCofsResponse,
	ExistingSite,
} from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { generateCCPUpgradeLink, generateHAMSUpgradeLink } from './create-upgrade-link';
import { getIdentityBaseUrl, getWacIntegrationUrl, isProduction } from './environment';
import { getRequestId } from './get-request-id';
import {
	Bundle,
	Edition,
	ProductKey,
	ProductData,
	ProductDataDetail,
	CollectionConstituentProductsMap,
	CollectionBundleNameToProductKeyMap,
	DefaultCollectionProductMap,
} from './products';
import { validatePrimaryCollectionProduct } from './validate-primary-collection-product';
/**
 * Returns continue param for redirect after login if present and valid
 *
 * @param location {Location} - current page location
 *
 * @returns {string} - continue URL
 */
export function getContinueParam(location: Location): string | null {
	const url = new URL(location.href);
	const continueParam = url.searchParams.get('continue');
	if (continueParam) {
		try {
			const continueUrl = new URL(continueParam);
			if (continueUrl.origin === getWacIntegrationUrl()) {
				// only use continue param if it's a valid WAC URL
				return decodeURIComponent(continueParam);
			}
		} catch {
			// continue param is not a valid URL
		}
	}
	return null;
}

// Ensure the correct path to the module
const JIRA_IMC_CAMPAIGN_KEY = 'I:imc-jira-twc-pm';
export const hasJiraImcCampaignKey = (value?: string | null) =>
	Boolean(value?.split('|').includes(JIRA_IMC_CAMPAIGN_KEY));

export const JIRA_PAID_FEATURES_PAGE_ID = 'paid-lp-jsw-features';

/**
 * Returns constructed subdirectory for URL path based on locale
 *
 * @param locale {string} - locale string from router
 *
 * @returns {string} - URL friendly locale subdirectory
 */
export function getLocalePath(locale?: string): string {
	return (locale || 'en') === 'en' ? '' : `/${locale}`;
}

/**
 * Returns login URL for existing product
 *
 * @param location {Location} - current page location
 *
 * @returns {string} - login URL as a string
 */
export const getLoginUrl = (location: Location): string => {
	const loginUrl = new URL('/login', getIdentityBaseUrl());
	const continueParam = getContinueParam(location);
	if (continueParam) {
		loginUrl.searchParams.append('continue', continueParam);
	} else {
		const defaultContinueUrl = new URL(
			'/gateway/api/start/authredirect',
			getWacIntegrationUrl(),
		).toString();
		loginUrl.searchParams.append('continue', defaultContinueUrl);
	}

	const isWacIcLoginEnabled = checkFeatureFlagGate(featureFlags.wacIcLogin);
	if (isWacIcLoginEnabled) {
		loginUrl.searchParams.append('application', 'wac--ic');
	}

	const finalLoginUrl = loginUrl.toString();
	return finalLoginUrl;
};

/**
 * Returns interstitial URL for identity verification during account creation
 * https://hello.atlassian.net/wiki/spaces/I/pages/5892458732/Interstitial+Signups+on+Identity+Guide+for+Consumers
 *
 * @param email {string} - email address to prefill
 * @param redirectTo {string} - URL to redirect to after interstitial
 * @param identityCobranding {string} - cobranding parameter for identity
 *
 * @returns {string} - login URL as a string
 */
export const getIdentityInterstitialUrl = (
	email: string,
	redirectTo: string,
	identityCobranding = 'wac',
	castleToken?: string,
): string => {
	const url = new URL('/signup/onboard', getIdentityBaseUrl());
	url.searchParams.append('email', email);
	url.searchParams.append('captcha_type', 'google-score');
	url.searchParams.append('application', identityCobranding);
	url.searchParams.append('continue', redirectTo);
	// Forwarded to Identity so it can call Castle's /v1/risk server-side
	if (castleToken) {
		url.searchParams.append('castle_token', castleToken);
	}
	return url.toString();
};

/**
 * Returns confirmation page redirect URL,
 * includes existing parameters & adds a specified `product` param
 *
 * @param baseUrl {string} - base URL for WAC
 * @param localePath {string} - locale string for url
 *
 * @returns {string} - confirmation page redirect URL
 */
export const getNameYourSiteRedirectUrl = ({
	bundle,
	edition,
	application,
	localePath = '',
	templateId,
	isEmbedded,
}: {
	bundle?: string;
	edition?: string;
	localePath?: string;
	application?: string;
	templateId?: string;
	isEmbedded?: boolean;
}): string => {
	const baseUrl = getWacIntegrationUrl();
	const siteNamePageUrl = '/try/cloud/signup';
	const searchParameters = new URLSearchParams(window.location.search);
	const isTwcEmbedded = bundle === 'teamwork-collection' && isEmbedded;

	if (!templateId) {
		templateId = searchParameters.get('templateId') ?? undefined;
	}
	const primaryCollectionProduct = searchParameters.get('primaryCollectionProduct');

	const cgsKey = searchParameters.get('cgsKey') || undefined;

	const params = new URLSearchParams();

	// bundle first
	if (bundle) {
		params.append('bundle', bundle);
	}

	// edition comes after bundle
	if (edition) {
		params.append('edition', edition);
	}

	if (templateId) {
		params.append('templateId', templateId);
	}

	if (application) {
		params.append('application', application);
	}

	if (cgsKey) {
		params.append('cgsKey', cgsKey);
	}

	if (isTwcEmbedded) {
		params.append('touchpoint', 'twc-embedded-signup');
	}

	['platform', 'continue', 'team_type', 'publicLinkId', 'editionIntent'].forEach((param) => {
		const value = searchParameters.get(param);
		if (value) {
			params.append(param, value);
		}
	});

	// if product is collection
	const collectionProductKey = bundle ? CollectionBundleNameToProductKeyMap[bundle] : undefined;

	const isCollectionBundle =
		!!collectionProductKey && collectionProductKey in CollectionConstituentProductsMap;

	if (isCollectionBundle) {
		const primaryProduct = validatePrimaryCollectionProduct(
			collectionProductKey,
			primaryCollectionProduct,
		);

		params.append('primaryCollectionProduct', primaryProduct);
	}

	return `${baseUrl}${localePath}${siteNamePageUrl}?${params}`;
};

type GetConfirmationPageUrlProps = {
	locale?: string;
	productKey: string;
	siteName: string;
	signupId?: string;
	edition: Edition;
	response: ActivateProductCcpResponse | ActivateProductCofsResponse;
	primaryCollectionProduct?: ProductKey; // product customer should be redirected to after signing up for a collection
};
/**
 * Returns confirmation page URL
 *
 * @param locale {string} - optional language locale for url (example /fr/ondemand/signup/confirmation)
 * @param productKey {string} - product key
 * @param siteName {string} - site name
 * @param signupId {string} - id used to track the e2e signup
 * @param response {object} - response object from product activation
 * @param edition {string} - edition of the product
 * @param primaryCollectionProduct {string} - product key of the primary collection product
 *
 * @returns {string} - confirmation page URL
 */
// eslint-disable-next-line complexity -- Existing URL assembly supports multiple product-specific signup flows.
export function getConfirmationPageUrl({
	locale,
	productKey,
	siteName,
	signupId,
	response,
	edition: originalEdition,
	primaryCollectionProduct,
}: GetConfirmationPageUrlProps) {
	// required query parameters from activation
	const redirectParams = new URLSearchParams();
	/**
	 * Rovo Dev (aka DEVAI) is a special case, it only has a single
	 * edition, standard. There is an outstanding DACI:
	 * https://hello.atlassian.net/wiki/spaces/uts/pages/5807772710/Rovo+Dev+Everywhere+SKU+DACI
	 * If/when this DACI is resolved and Rovo Dev supports the free
	 * edition, this special case can then be removed.
	 */
	let edition = originalEdition;
	if (productKey === ProductKey.DEVAI) {
		edition = 'standard';
	}
	// SPICE-1705: prefer the provisioned edition reported by the server
	// over the form-side edition, since the two can diverge (e.g. Reverse
	// Trial signs the user up for Free but provisions Premium).
	// DEVAI is excluded so the "standard" invariant above is preserved.
	if (productKey !== ProductKey.DEVAI && response.service === 'ccp') {
		const provisionedEdition = (response as ActivateProductCcpResponse).metadata?.products?.[0]
			?.edition;
		if (provisionedEdition) {
			edition = provisionedEdition as Edition;
		}
	}
	redirectParams.append('ondemandurl', siteName);
	redirectParams.append('products', productKey);
	redirectParams.append('edition', edition);

	const isCollection = DefaultCollectionProductMap[productKey];

	// excluding orchestrationId param in URL makes confirmation page use polling instead of graphql subscription
	if (response.orchestrationId && !isCollection) {
		redirectParams.append('orchestrationId', response.orchestrationId);
	}

	if (signupId) {
		redirectParams.append('signupId', signupId);
	}

	if (response.service === 'ccp') {
		const ccpResponse = response as ActivateProductCcpResponse;
		const requestId = ccpResponse.metadata?.provisionRequestId;
		redirectParams.append('platform', 'ccp');
		redirectParams.append('provisioningRequestId', requestId);
	} else {
		const cofsResponse = response as ActivateProductCofsResponse;
		const requestId = getRequestId(cofsResponse.progressUri);
		redirectParams.append('cloudId', cofsResponse.cloudId);
		if (requestId) {
			redirectParams.append('requestId', requestId);
		}
	}

	if (isCollection) {
		const primaryProduct = validatePrimaryCollectionProduct(productKey, primaryCollectionProduct);

		redirectParams.append('primaryCollectionProduct', primaryProduct);
	}

	// pass through current query parameters
	const searchParameters = new URLSearchParams(window.location.search);

	const n2n = searchParameters.get('n2n') === 'true';
	if (n2n) {
		redirectParams.append('n2n', 'true');
	}

	const signupSource = searchParameters.get('signupSource');
	if (signupSource) {
		redirectParams.append('signupSource', signupSource);
	}

	const team_type = searchParameters.get('team_type');
	if (team_type) {
		redirectParams.append('team_type', team_type);
	}

	const templateId = searchParameters.get('templateId');
	if (templateId) {
		redirectParams.append('templateId', templateId);
	}

	const cgsKey = searchParameters.get('cgsKey');
	if (cgsKey) {
		redirectParams.append('cgsKey', cgsKey);
	}

	const localePath = getLocalePath(locale);

	return `${localePath}/ondemand/signup/confirmation?${redirectParams}`;
}

/**
 * Returns the confirmation page URL for the deferred site naming variant.
 *
 * Unlike `getConfirmationPageUrl`, this is called *before* product activation has run —
 * so no `ondemandurl`, `provisioningRequestId`, `signupId`, or `orchestrationId` are included.
 * The confirmation page hook (`useDeferSiteActivation`) is responsible for calling
 * `getRecommendedSiteName` + `activateProduct` and populating local state from the response.
 *
 * Pass-through params mirror the trailing block of `getConfirmationPageUrl` so the deferred
 * arm preserves the same surface (templateId, cgsKey, signupSource, team_type, n2n).
 *
 * @returns {string} - confirmation page URL with `deferSiteNaming=true` flag
 */
export function getDeferSiteNamingConfirmationPageUrl({
	locale,
	productKey,
	edition,
}: {
	locale?: string;
	productKey: string;
	edition: string;
}): string {
	const redirectParams = new URLSearchParams();
	redirectParams.append('deferSiteNaming', 'true');
	redirectParams.append('products', productKey);
	redirectParams.append('edition', edition);

	// pass through current query parameters (mirrors getConfirmationPageUrl).
	// Guarded against SSR — `window` is undefined on the server. In practice
	// this helper is only invoked from event handlers on the client, but the
	// guard keeps the function safe to call from anywhere.
	if (typeof window !== 'undefined') {
		const searchParameters = new URLSearchParams(window.location.search);

		const n2n = searchParameters.get('n2n') === 'true';
		if (n2n) {
			redirectParams.append('n2n', 'true');
		}

		const signupSource = searchParameters.get('signupSource');
		if (signupSource) {
			redirectParams.append('signupSource', signupSource);
		}

		const team_type = searchParameters.get('team_type');
		if (team_type) {
			redirectParams.append('team_type', team_type);
		}

		const templateId = searchParameters.get('templateId');
		if (templateId) {
			redirectParams.append('templateId', templateId);
		}

		const cgsKey = searchParameters.get('cgsKey');
		if (cgsKey) {
			redirectParams.append('cgsKey', cgsKey);
		}
	}

	const localePath = getLocalePath(locale);

	// Return an absolute URL so identity onboarding can validate the
	// `continue` redirect target against its trusted-domain allowlist
	// and forward it verbatim (preserving `deferSiteNaming=true` and
	// other custom params). When the continueUrl is a relative path,
	// identity strips unknown query params during sanitization. This
	// matches the behavior of `getNameYourSiteRedirectUrl` (the default
	// getContinueUrl path used in the control arm), which also returns
	// an absolute URL via `getWacIntegrationUrl()`.
	return `${getWacIntegrationUrl()}${localePath}/ondemand/signup/confirmation?${redirectParams}`;
}

/**
 * Returns primary (first) product key from bundle
 *
 * @param {Bundle} bundle - product bundle
 *
 * @returns {string} - product key
 */
export const getPrimaryProductKeyFromBundle = (bundle: Bundle) => {
	switch (bundle) {
		case 'confluence':
			return ProductKey.CONFLUENCE;
		case 'jira-service-management':
		case 'jira-service-management-jira-software':
			return ProductKey.JIRA_SERVICE_MANAGEMENT;
		case 'devai':
			return ProductKey.DEVAI;
		default:
			return ProductKey.JIRA_SOFTWARE;
	}
};

/**
 * Returns product data from bundle
 *
 * @param bundle {string} - product bundle
 *
 * @returns {ProductDataDetail[]} - product key
 */
export const getProductsFromBundle = (bundle: Bundle): ProductDataDetail[] => {
	switch (bundle) {
		case 'confluence':
			return [ProductData[ProductKey.CONFLUENCE]];
		case 'jira-service-management':
			return [ProductData[ProductKey.JIRA_SERVICE_MANAGEMENT]];
		case 'jira-service-management-jira-software':
			return [
				ProductData[ProductKey.JIRA_SERVICE_MANAGEMENT],
				ProductData[ProductKey.JIRA_SOFTWARE],
			];
		case 'devai':
			return [ProductData[ProductKey.DEVAI]];
		default:
			return [ProductData[ProductKey.JIRA_SOFTWARE]];
	}
};

/**
 * Returns URL to switch account page
 *
 * @returns {string} - URL
 */
export const getSwitchAccountUrl = (): string => {
	const params = new URLSearchParams({
		continue: window.location.href,
	});
	return `${getIdentityBaseUrl()}/login/select-account?${params}`;
};

/**
 * Returns the Loom SSO URL that authenticates the user against Atlassian and
 * lands them directly in their Loom workspace for the given cloud id.
 *
 * Mirrors the canonical Loom redirect used by the signup confirmation flow:
 *   https://loom.com/api/auth/atlassian?redirect_after=https://www.loom.com/workspace/cloud/{cloudId}
 * (stage.loom.com / stage.www.loom.com in non-production).
 *
 * @param cloudId {string} - cloud id of the site whose Loom workspace to open
 * @returns {string} - Loom SSO deep-link URL
 */
export const getLoomSsoUrl = (cloudId: string): string => {
	const authHost = isProduction() ? 'https://loom.com' : 'https://stage.loom.com';
	const workspaceHost = isProduction() ? 'https://www.loom.com' : 'https://stage.www.loom.com';
	const redirectAfter = `${workspaceHost}/workspace/cloud/${cloudId}`;
	return `${authHost}/api/auth/atlassian?redirect_after=${encodeURIComponent(redirectAfter)}`;
};

/**
 * Returns URL to upgrade product via AdminHub
 *
 * @param site {ExistingSite} - existing site object
 * @param productKey {string} - product key to upgrade
 * @param edition {string} - target edition to upgrade to
 *
 * @returns {string} - URL
 */
export const getAdminHubUpgradeProductUrl = async (
	site: ExistingSite,
	productKey: string,
	targetEdition: string,
) => {
	let billingSourceSystem, entitlementId;

	const productToUpgrade = site.products.find((product) => product.productKey === productKey);

	if (productToUpgrade) {
		billingSourceSystem = productToUpgrade.billingSourceSystem;
		entitlementId = productToUpgrade.entitlementId;
	} else {
		const collectionToUpgrade = site.products.find(
			(product) => product.partOfCollection === productKey,
		);

		if (collectionToUpgrade) {
			billingSourceSystem = collectionToUpgrade.billingSourceSystem;
			entitlementId = collectionToUpgrade.collectionEntitlementId;
		}
	}

	if (!entitlementId || !billingSourceSystem) {
		throw new Error(`Cannot find product ${productKey} with entitlementId or billingSourceSystem`);
	}

	if (billingSourceSystem === 'CCP') {
		return await generateCCPUpgradeLink(entitlementId, targetEdition);
	}

	return generateHAMSUpgradeLink(entitlementId, productKey, targetEdition);
};

/**
 * Returns URL to add product to site via crossflow
 *
 * @returns {string} - URL
 */
export const getCrossFlowActivationUrl = ({
	cloudId,
	edition: originalEdition,
	productKey,
	siteName,
	hasPaidTargetEditionRequiringConfirmationScreen,
}: {
	cloudId: string;
	edition?: string;
	productKey: string;
	siteName: string;
	hasPaidTargetEditionRequiringConfirmationScreen?: boolean;
}) => {
	const startUrl = new URL(
		isProduction() ? 'https://start.atlassian.com' : 'https://start.stg.atlassian.com',
	);
	startUrl.searchParams.set('startTrialProduct', productKey);
	startUrl.searchParams.set('startTrialCloudId', cloudId);
	startUrl.searchParams.set('startTrialInstance', siteName);
	startUrl.searchParams.set('cloudId', cloudId);

	/**
	 * Rovo Dev (aka DEVAI) is a special case, it only has a single
	 * edition, standard. There is an outstanding DACI:
	 * https://hello.atlassian.net/wiki/spaces/uts/pages/5807772710/Rovo+Dev+Everywhere+SKU+DACI
	 * If/when this DACI is resolved and Rovo Dev supports the free
	 * edition, this special case can then be removed.
	 */
	let edition = originalEdition;
	if (productKey === ProductKey.DEVAI) {
		edition = 'standard';
	}

	const searchParameters = new URLSearchParams(window?.location.search);
	const signupSource = searchParameters.get('signupSource');

	const shouldForceConfirmationScreen =
		edition === 'free' &&
		signupSource !== 'pricing' &&
		hasPaidTargetEditionRequiringConfirmationScreen &&
		[
			ProductKey.CONFLUENCE,
			ProductKey.JIRA_SOFTWARE,
			ProductKey.JIRA_PRODUCT_DISCOVERY,
			ProductKey.JIRA_SERVICE_DESK,
			ProductKey.SERVICE_COLLECTION,
		].includes(productKey as ProductKey);

	if (shouldForceConfirmationScreen) {
		startUrl.searchParams.set('forceConfirmationScreen', 'true');
	} else if (edition) {
		startUrl.searchParams.set('edition', edition);
	}

	// pass through current query parameters
	const templateId = searchParameters.get('templateId');
	if (templateId) {
		startUrl.searchParams.set('templateId', templateId);
	}

	const billingCode = searchParameters.get('billingMode');
	if (billingCode) {
		startUrl.searchParams.set('billingMode', billingCode);
	}

	const billingMode = searchParameters.get('billingMode');
	if (billingMode) {
		startUrl.searchParams.set('billingMode', billingMode);
	}

	return startUrl.toString();
};
